import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import store from '@state/store'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import {
  currentFamilyGroup,
  resolveConsolidationScope,
} from '@modules/consolidator/utils/consolidation-scope/consolidation-scope'
import {
  diffTimeMinutes,
  formatQueryParams,
  safeJsonParse,
} from '@utils/Format'
import {
  buildRequestKey,
  dedupeInFlight,
  isSameScope,
} from '@modules/consolidator/utils/Api/Api'
import { adaptWalletAnalysisResponse } from '@modules/consolidator/utils/adapt-wallet-analysis-response/adapt-wallet-analysis-response'
import {
  buildWalletOptionsFields,
  filterOptionsFieldsForProfile,
} from '@modules/consolidator/utils/build-wallet-options-fields/build-wallet-options-fields'
import { serializeWalletAnalysisParams } from '@modules/consolidator/utils/serialize-wallet-analysis-params/serialize-wallet-analysis-params'

class APIConsolidatorWalletAnalysis extends APITemplate {
  constructor() {
    super()

    this.walletAnalysisRegistry = {}
    this.latestScopeKey = null
    this.loadedClientId = null
  }

  carregar({
    id_cliente_app,
    is_client,
    data_requested,
    forceUpdate,
    ...filters
  } = {}) {
    const grupo = currentFamilyGroup()
    const escopo = resolveConsolidationScope({
      grupo,
      isClient: is_client,
      idClienteApp: id_cliente_app,
    })

    const { error, params } = serializeWalletAnalysisParams({
      id_cliente_app,
      is_client: escopo.allowRequestWithoutClient,
      data_requested,
      ...filters,
    })

    if (error) {
      return Promise.resolve(
        adaptWalletAnalysisResponse({
          status: 400,
          value: { status: 400, message: error },
        })
      )
    }

    const scopeKey = escopo.scopeKey
    this.latestScopeKey = scopeKey

    const cached = this.getCachedResponse({ forceUpdate, scopeKey })
    if (cached) {
      store.dispatch('consolidator/setWalletAnalysisLoading', false)
      return Promise.resolve(cached)
    }

    if (!isSameScope(this.loadedClientId, scopeKey)) {
      store.dispatch('consolidator/setWalletAnalysis', null)
    }

    return dedupeInFlight({
      registry: this.walletAnalysisRegistry,
      key: buildRequestKey({ scopeKey, params }),
      run: () =>
        this._fetchAndDispatch({
          url: escopo.url,
          params,
          scopeKey,
        }),
    })
  }

  async _fetchAndDispatch({ url: baseUrl, params, scopeKey }) {
    store.dispatch('consolidator/setWalletAnalysisLoading', true)

    const url = baseUrl + formatQueryParams(params)

    try {
      const adapted = await con
        .get(url)
        .then(safeJsonParse)
        .then(adaptWalletAnalysisResponse)

      if (this.latestScopeKey !== scopeKey) {
        return store.getters['consolidator/walletAnalysis']
      }

      this.dispatchAdapted(adapted)
      if (adapted && adapted.ok) this.loadedClientId = scopeKey

      return adapted
    } finally {
      if (this.latestScopeKey === scopeKey) {
        store.dispatch('consolidator/setWalletAnalysisLoading', false)
      }
    }
  }

  getCachedResponse({ forceUpdate, scopeKey } = {}) {
    const isSameClient = isSameScope(this.loadedClientId, scopeKey)

    if (isSameClient && !forceUpdate) {
      const isCacheValid = !diffTimeMinutes(
        store.getters['consolidator/walletAnalysisLastUpdate'],
        MINUTES_TIMEOUT_REQUESTS
      )
      if (isCacheValid) {
        return store.getters['consolidator/walletAnalysis']
      }
    }

    return null
  }

  dispatchAdapted(adapted) {
    store.dispatch(
      'consolidator/setWalletAnalysis',
      adapted && adapted.ok ? adapted : null
    )
  }

  async loadOptions({ id_cliente_app, is_client } = {}) {
    const grupo = store.getters['consolidator/currentFamilyGroup']
    if (!grupo && !is_client && !id_cliente_app) return []
    store.dispatch('consolidator/setWalletAnalysisOptionsFieldsLoading', true)
    try {
      const { url } = resolveConsolidationScope({
        grupo,
        isClient: is_client,
        idClienteApp: id_cliente_app,
      })
      const r = await con.options(url)
      const clientData = (r && (r.client_data || r.group_data)) || null
      const fields = filterOptionsFieldsForProfile(
        buildWalletOptionsFields(clientData),
        { isClient: is_client }
      )
      store.dispatch('consolidator/setWalletAnalysisOptionsFields', fields)
      return fields
    } finally {
      store.dispatch(
        'consolidator/setWalletAnalysisOptionsFieldsLoading',
        false
      )
    }
  }
}

const apiConsolidatorWalletAnalysis = new APIConsolidatorWalletAnalysis()
export { apiConsolidatorWalletAnalysis }
